/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { IButton, IconButton } from '@fluentui/react/lib/Button';
import { IModal, Modal } from '@fluentui/react/lib/Modal';
import * as React from 'react';
import { modalRefVar } from '../../../apollo/reactiveVariables/TemplateManagementVariables';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { getModalStyles } from './TemplateManagementModal.styles';

export interface TemplateManagementModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    onRenderContent?: () => JSX.Element;
}

const TemplateManagementModal: React.FC<TemplateManagementModalProps> = (
    props: TemplateManagementModalProps
): JSX.Element => {
    const { isOpen, onRenderContent, onDismiss } = props;

    const loc = getAppHost().getLocale();
    const modalRef = React.useRef<IModal>(null);
    const cancelRef = React.createRef<IButton>();

    React.useEffect((): void => {
        modalRefVar(modalRef);
        cancelRef?.current?.focus();
    }, [modalRef, cancelRef]);

    return (
        <Modal
            componentRef={modalRef}
            styles={getModalStyles()}
            isBlocking={true}
            isOpen={isOpen}
            forceFocusInsideTrap={false}
            onDismiss={onDismiss}>
            <IconButton
                iconProps={{
                    iconName: 'Cancel',
                }}
                ariaLabel={loc.strings.modal_close_aria_label}
                title={loc.strings.modal_close_aria_label}
                onClick={onDismiss}
                className='modal-dismiss-button'
                componentRef={cancelRef}
            />
            {onRenderContent && onRenderContent()}
        </Modal>
    );
};

export const TemplateManagementModalComponent = React.memo(TemplateManagementModal);
